import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, FoldVertical, UnfoldVertical, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { formatMoney } from '@/lib/infra/formatters';
import { useMarketStore } from '../../stores/marketStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useAuthStore } from '@/stores/authStore';
import { useOrderEntryModel } from './useOrderEntryModel';
import type { OrderEntryTarget } from './orderEntryTarget';
import { ExchangeOrderBook } from '@/components/organisms/ExchangeOrderBook';
import { OrderbookFreshness } from '@/components/atoms/OrderbookFreshness';
import { EventStateWarning } from '@/components/atoms/EventStateWarning';
import {
  useCanonicalGameState,
  useEventStateCertainty,
} from '@/hooks/ui/useEventStateCertainty';
import { useOscarKillPreview } from '@/hooks/useOscarBlockers';
import { useHydraOrderbookWarmup } from '@/hooks/useHydraOrderbookWarmup';
import { roundToCentiCents } from '@/lib/pricing/subCent';
import { buildSide } from '@/lib/trading/bookSides';
import type { GameRow, TeamSide, MarketSide, VenueBook } from './types';
import { LiveBadge, UnknownStatusBadge } from './GameTimeIndicators';
import { resolveLiveDisplayState } from '@/lib/sport/liveDisplayState';
import { BotModeSwitch } from './BotSpawnForm';
import { eventTickerOfMarket, sportOfEventTicker } from '../../lib/vanilla/slate';
import { GameOpenOrders } from './GameOpenOrders';
import {
  americanOdds,
  bestPriceCentsForOrderSide,
  bookCaptionName,
  gameMarketKind,
  outcomeSideLabel,
} from './tradeModalBook';
import {
  LADDER_MAX_LEGS,
  validateLeg,
  type LadderLeg,
} from './orderLadder';
import type { OrderTicketQuote } from './orderEntryEconomics';
import { type OscarReleasePrefill } from './oscar';
import { OscarSequenceReceipt } from './OscarSequenceReceipt';
import { OrderResultBanner } from './OrderResultBanner';
import { OneClickReceipts } from './OneClickReceipts';
import { POLY_MIN_ORDER_CONTRACTS } from '@/lib/polymarket/closePosition';
import { buildQuickSizeChips } from './quickSizeChips';
import { MiniLineStrip } from '../SpreadsTotals/MiniLineStrip';
import { PairContextCard } from './PairContextCard';
import { GamePositionSnapshot } from './GamePositionSnapshot';
import { useConsensusGlow, type ConsensusSideGlow } from '@/hooks/ui/useConsensusGlow';
import { useChangeKey } from '@/hooks/ui/useFlash';
import { TrendArrow } from '@/components/atoms/TrendArrow';
import { SharpStar } from '@/components/atoms/SharpStar';
import { CONSENSUS_TREND_WINDOW_MS, type ConsensusTrendState } from '@/lib/consensusTrend';
import { useDeadlineTick } from '@/hooks/ui/useDeadlineTick';
import {
  fairAmericanText,
  fairPctText,
  opAnchorGlowDelayMs,
  opIsStale,
  opNoDeltaText,
  opWithdrawalAgeDeadline,
  opWithdrawalTitle,
  OP_STALE_MS,
  type OpFairSideView,
  type OpFairView,
} from '@/lib/opOdds';

// Phantom-level filter + display-side ladder builder live in
// lib/trading/bookSides (shared with the Cockpit's live books).
/** Real best-bid price for the requested side (YES or NO). Reads the actual
 *  side ladder via bestPriceCentsForOrderSide rather than inverting YES — so
 *  this strip matches the orderbook the user is staring at, even when
 *  YES + NO don't sum to 100¢ across a wide spread. */
function formatSidePriceCents(book: VenueBook | undefined | null, side: MarketSide): string {
  const cents = bestPriceCentsForOrderSide(book ?? undefined, side);
  return cents == null ? '—' : `${cents}¢`;
}

/** Render Unabated consensus as a fair-probability percentage. UNLIKE
 *  K and P (which are order-book prices that flip semantics with the
 *  YES/NO toggle — NO is expensive for underdogs), U is a probability:
 *  the % chance this team wins, full stop. Same value whether the
 *  user is bidding YES or NO. Matches what the consensus chart shows. */
function formatConsensusCents(yesCents: number | null | undefined): string {
  if (yesCents == null || !Number.isFinite(yesCents)) return '—';
  return `${Math.round(yesCents)}%`;
}

// ── UnabatedConsensusRow — the panel's U row with tenths-trend cues ──
// Split out so useConsensusGlow's 4 Hz decay ticker re-renders just this
// strip (not the whole panel) and the hook sits safely under the row's
// conditional render. Same treatments as the dashboard's Odds column
// (OddsPctText): recency brightness on the number, trend arrow with
// delta-mass tone, 4-beat integer glow, and the superscript gold star
// while the consensus is Pinnacle-anchored (sharpBookUsed).
function UnabatedConsensusRow({
  unabated,
}: {
  unabated: NonNullable<GameDetailPanelProps['unabated']>;
}) {
  const glow = useConsensusGlow(unabated.trend ?? null);
  // The trend state tracks Unabated's own away/home slots; the caller
  // already swapped the cents to the panel's columns, so mirror that swap
  // when reading per-side glow.
  const awayGlow = glow == null ? null : unabated.swapped ? glow.home : glow.away;
  const homeGlow = glow == null ? null : unabated.swapped ? glow.away : glow.home;
  const sharp = unabated.sharpBookUsed ?? glow?.sharpBookUsed ?? null;
  const title = `Unabated consensus fair-probability (full game)${
    sharp == null ? '' : sharp ? ' · Pinnacle-anchored (sharp)' : ' · field books only'
  }`;
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-xs font-semibold text-amber-400" title={title}>
        U
      </div>
      <UnabatedConsensusCell
        cents={unabated.awayCents}
        tenths={unabated.awayTenths}
        glow={awayGlow}
        sharp={sharp}
      />
      <UnabatedConsensusCell
        cents={unabated.homeCents}
        tenths={unabated.homeTenths}
        glow={homeGlow}
        sharp={sharp}
      />
    </div>
  );
}

// ── OpOddsRow — the panel's OP row (OddsPapi de-vigged fair values) ──
// Sits IMMEDIATELY below the U row. Like U it is a fair-probability row —
// it does NOT flip with the YES/NO toggle (the component takes no side
// input at all, by construction) — and with D4 amended its semantics
// match U exactly: hydra-computed de-vigged fair values (rule 6c — g1
// never computes them). Renders `[consensus] / pinnacle` per side: the
// consensus fair % primary, the anchor book's de-vig as the small
// secondary; ANCHOR-ONLY (dotted underline) when a side has no consensus
// value; a side with neither renders `—`. Teal "O" label (U=amber,
// K=blue, P=purple). Freshness is per displayed value (rule 7 as
// amended): consensus from consensus.lastUpdateMs, the anchor from that
// quote's own updatedAtMs, INDEPENDENTLY — surfaced as the factual
// "no delta received in Xm" hover annotation only, NO opacity dim
// (Dan 2026-08-04; matches the U row). Exported for the focused row
// tests.
export function OpOddsRow({
  opOdds,
  trend = null,
}: {
  opOdds: NonNullable<GameDetailPanelProps['opOdds']>;
  /** OP's own trend bucket (marketStore `useOpTrendFor`) — velocity
   *  arrows + recency brightness, mirroring the U row's treatment. */
  trend?: ConsensusTrendState | null;
}) {
  // Same engine + shared ticker the U row uses; the trend tracks
  // PROVIDER slots, so the swap flag maps glow sides to panel columns.
  // Grey arrow rests OP_STALE_MS (sparse feed) — see the dashboard cell.
  const glow = useConsensusGlow(trend, OP_STALE_MS);
  const awayGlow = glow == null ? null : opOdds.swapped ? glow.home : glow.away;
  const homeGlow = glow == null ? null : opOdds.swapped ? glow.away : glow.home;
  const withdrawn = opOdds.pinnacleWithdrawn;
  // Mounted-freshness clock authority (G1 round 3): one bounded
  // re-render at each upcoming boundary — the per-value "no delta
  // received" annotations and the arrow's grey-rest hide — so a quiet
  // mounted row crosses its thresholds without unrelated re-renders.
  useDeadlineTick([
    opOdds.consensusLastUpdateMs != null ? opOdds.consensusLastUpdateMs + OP_STALE_MS : null,
    opOdds.anchorUpdatedAtMs != null ? opOdds.anchorUpdatedAtMs + OP_STALE_MS : null,
    // Round-1 finding 2: the withdrawal age must advance while mounted.
    withdrawn != null ? opWithdrawalAgeDeadline(withdrawn.sinceMs) : null,
    ...(['away', 'home', 'draw'] as const).map((s) =>
      trend != null && trend[s].lastDeltaMs > 0
        ? trend[s].lastDeltaMs + CONSENSUS_TREND_WINDOW_MS + OP_STALE_MS
        : null,
    ),
  ]);
  const anchorStale = opOdds.anchorUpdatedAtMs != null && opIsStale(opOdds.anchorUpdatedAtMs);
  const consensusStale =
    opOdds.consensusLastUpdateMs != null && opIsStale(opOdds.consensusLastUpdateMs);
  const anchorName = opOdds.anchorBookmaker ?? 'pinnacle';
  const title = `OddsPapi consensus (de-vigged${
    opOdds.contributingBooks != null ? `, ${opOdds.contributingBooks} books` : ''
  }${opOdds.pinnacleAnchored ? `, ${anchorName}-anchored` : ''}) / de-vigged ${anchorName} — fair win %, hydra-computed.${
    consensusStale && opOdds.consensusLastUpdateMs != null
      ? ` ${opNoDeltaText('Consensus', opOdds.consensusLastUpdateMs)}`
      : ''
  }${
    anchorStale && opOdds.anchorUpdatedAtMs != null
      ? ` ${opNoDeltaText(anchorName, opOdds.anchorUpdatedAtMs)}`
      : ''
  }${withdrawn != null ? ` ${opWithdrawalTitle(withdrawn)}.` : ''}`;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="op-odds-row">
      <div className="text-xs font-semibold text-teal-400" title={title}>
        O
      </div>
      <OpOddsCell
        side={opOdds.away}
        consensusStale={consensusStale}
        anchorStale={anchorStale}
        anchorUpdatedAtMs={opOdds.anchorUpdatedAtMs}
        withdrawn={withdrawn}
        glow={awayGlow}
        title={title}
      />
      <OpOddsCell
        side={opOdds.home}
        consensusStale={consensusStale}
        anchorStale={anchorStale}
        anchorUpdatedAtMs={opOdds.anchorUpdatedAtMs}
        withdrawn={withdrawn}
        glow={homeGlow}
        title={title}
      />
    </div>
  );
}

function OpOddsCell({
  side,
  consensusStale,
  anchorStale,
  anchorUpdatedAtMs,
  withdrawn = null,
  glow = null,
  title,
}: {
  side: OpFairSideView;
  consensusStale: boolean;
  anchorStale: boolean;
  anchorUpdatedAtMs: number | null;
  /** Rule 12: pinnacle's withdrawal entry — the pinnacle slot renders
   *  the red struck last-known value while present. */
  withdrawn?: NonNullable<GameDetailPanelProps['opOdds']>['pinnacleWithdrawn'];
  glow?: ConsensusSideGlow | null;
  title: string;
}) {
  // Per-side mode mirrors the dashboard cell: no consensus value for this
  // side ⇒ anchor-only rendering off the anchor's own freshness. Pinnacle
  // renders at EQUAL size beside the consensus (Dan 2026-08-03), with the
  // 6s update-glow halo keyed per quote (Dan 2026-08-04).
  const pct = fairPctText(side.consensusTenths);
  const anchorPct = fairPctText(side.anchorFairTenths);
  const anchorOnly = pct == null;
  // American sublines — ONE PER VALUE (Dan, 2026-08-17), so each
  // translation sits directly under the % it translates: consensus's
  // American under the consensus %, the anchor's under the anchor %.
  // Both remain display-only translations of hydra values (rule 6c).
  const consensusAmerican = anchorOnly
    ? null
    : fairAmericanText(side.consensusTenths, side.consensusCents);
  const anchorAmerican = fairAmericanText(side.anchorFairTenths, null);
  const primaryStale = anchorOnly ? anchorStale : consensusStale;
  const struckPct = withdrawn != null ? fairPctText(side.withdrawnLastTenths) : null;
  const empty = pct == null && anchorPct == null && withdrawn == null;
  return (
    <div
      className="text-center font-mono text-xs"
      title={title}
      data-op-mode={empty ? undefined : anchorOnly ? 'anchor-only' : 'consensus'}
      data-op-stale={(!empty && primaryStale) || undefined}
      data-op-anchor-stale={(anchorPct != null && anchorStale) || undefined}
    >
      {empty ? (
        <span className="text-muted-foreground/40">—</span>
      ) : (
        /* items-start: each value is now its own two-line column (% over
           its American), so the top line — arrow, %, separator, % — stays
           rank-aligned while the Americans align under their own values
           (Dan, 2026-08-17). */
        <span className="inline-flex items-start gap-1 leading-tight">
          {pct != null && (
            <TrendArrow
              direction={glow?.direction ?? 0}
              strength={glow?.strength ?? 0}
              sizeClass="text-xs"
            />
          )}
          {pct != null && (
            <span className="inline-flex flex-col items-center">
              <span
                className="text-foreground/80"
                style={{ opacity: 0.8 + 0.2 * (glow?.recency ?? 0) }}
              >
                {pct}%
              </span>
              {consensusAmerican != null && (
                <span className="text-muted-foreground text-[11px]" data-op-consensus-american>
                  {consensusAmerican}
                </span>
              )}
            </span>
          )}
          {pct != null && (anchorPct != null || withdrawn != null) && (
            <span className="text-muted-foreground/50 text-[10px]">/</span>
          )}
          {anchorPct != null && anchorUpdatedAtMs != null && (
            <span className="inline-flex flex-col items-center">
              <span
                key={`f${anchorUpdatedAtMs}`}
                className={cn(
                  'animate-op-anchor-glow text-muted-foreground',
                  anchorOnly && 'border-b border-dotted border-teal-400/60',
                )}
                style={{ animationDelay: `${opAnchorGlowDelayMs(anchorUpdatedAtMs)}ms` }}
                data-op-anchor-value
              >
                {anchorPct}%
              </span>
              {anchorAmerican != null && (
                <span className="text-muted-foreground text-[11px]" data-op-anchor-american>
                  {anchorAmerican}
                </span>
              )}
            </span>
          )}
          {/* THE STRIKETHROUGH (rule 12) — see the dashboard cell. */}
          {withdrawn != null && anchorPct == null && (
            <span
              className="text-red-400 line-through decoration-red-400/80"
              title={opWithdrawalTitle(withdrawn)}
              data-op-withdrawn={withdrawn.reason}
            >
              {struckPct != null ? `${struckPct}%` : '—'}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

function UnabatedConsensusCell({
  cents,
  tenths,
  glow,
  sharp,
}: {
  cents: number | null | undefined;
  tenths?: number | null;
  glow: ConsensusSideGlow | null;
  sharp: boolean | null;
}) {
  // Baseline matches the old static text-foreground/80 tint; frame arrival
  // (tenth-only included) rides it to full brightness, easing back in ~10s.
  const opacity = 0.8 + 0.2 * (glow?.recency ?? 0);
  // 4-beat amber halo stays keyed on the INTEGER — the tenths digit updates
  // quietly (recency brightness covers it); at the 250ms coalesced cadence a
  // tenths-keyed halo would strobe.
  const integerGlowKey = useChangeKey(cents == null ? null : Math.round(cents));
  // American-odds translation of the same fair prob, small muted line under
  // the % (matches the dashboard Odds column). Display-only.
  const displayCents = tenths != null ? tenths / 10 : cents;
  const american =
    displayCents != null && Number.isFinite(displayCents) && displayCents > 0 && displayCents < 100
      ? americanOdds(displayCents)
      : null;
  return (
    <div className="text-center font-mono text-xs">
      <span className="inline-flex flex-col items-center leading-tight">
        <span className="inline-flex items-center gap-0.5">
          <TrendArrow direction={glow?.direction ?? 0} strength={glow?.strength ?? 0} />
          <span
            key={integerGlowKey}
            className={cn(
              'text-foreground transition-opacity duration-200 ease-linear',
              integerGlowKey > 0 && 'animate-odds-integer-glow',
            )}
            style={{ opacity }}
          >
            {tenths != null ? `${(tenths / 10).toFixed(1)}%` : formatConsensusCents(cents)}
          </span>
          <SharpStar sharp={sharp} recency={glow?.recency ?? 0} className="text-[8px]" />
        </span>
        {american != null && american !== '—' && (
          <span className="text-muted-foreground text-[10px]" style={{ opacity }}>
            {american}
          </span>
        )}
      </span>
    </div>
  );
}

/** The (team, venue, line-ticker) the order ticket's price input was last
 *  seeded from. Tracked across renders so {@link shouldReseedPriceOnBookChange}
 *  can distinguish a line swap from a team/venue switch. */
export interface ActiveBookSelection {
  ticker: string;
  side: TeamSide;
  venue: 'kalshi' | 'polymarket';
}

/** The book the panel actually trades for a given (team side, venue) — the
 *  single rule behind its price strip, orderbook, and order ticket. Exported
 *  so callers that must guarantee WHICH ticker an intent will trade (the
 *  PositionTracker's exact-ticker targeting for advance/futures legs, issue
 *  #98) can assert against the panel's own derivation instead of
 *  re-implementing it and drifting. */
export function activeVenueBook(
  game: GameRow,
  side: TeamSide,
  venue: 'kalshi' | 'polymarket',
): VenueBook | null {
  const pair = venue === 'kalshi' ? game.kalshi : game.polymarket;
  if (!pair) return null;
  return side === 'away' ? pair.away : pair.home;
}

/**
 * Should an active-book change re-seed the price input with top-of-book?
 *
 * Re-seed ONLY when the *line ticker* changed while the (team, venue) held
 * steady — i.e. a spread/total chip swapped the line under the same
 * selection, so the old price belongs to a different bet and there's nothing
 * the trader explicitly picked to preserve.
 *
 * A ticker change that rides along with a team or venue switch is NOT a line
 * swap, and must NOT re-seed:
 *   - tab toggle (applyTeamToggle / applyVenueToggle already wrote the hint), or
 *   - a level click on the *other* team/venue's book — the butterfly ladder
 *     shows both teams + both venues at once — where the tradeIntent prefill
 *     just wrote the exact price the trader clicked.
 * Re-seeding there clobbers that selection with top-of-book. That was the
 * "first click into a ladder shows top-of-book, later clicks stick" bug: the
 * first click is the one that flips selectedSide/panelVenue, so it tripped
 * the reseed; subsequent clicks on the now-selected book didn't.
 *
 * `prev` is null on mount — initial seeding is the tradeIntent prefill's job.
 */
export function shouldReseedPriceOnBookChange(
  prev: ActiveBookSelection | null,
  next: { ticker: string | null; side: TeamSide; venue: 'kalshi' | 'polymarket' },
): boolean {
  if (prev === null) return false; // mount
  if (next.ticker === null) return false; // book dropped out
  if (prev.ticker === next.ticker) return false; // no line change
  return prev.side === next.side && prev.venue === next.venue;
}

export interface TradeIntent {
  side: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
  /** Pre-fill the order ticket's price input. Set when the click came
   *  from a specific price pill or orderbook level; omitted when the
   *  panel was opened by a row-expand toggle with no price context. */
  prefillPrice?: number;
  /** Pre-select YES/NO on the order ticket. Comes from the click target
   *  (e.g. a YES bid pill) rather than the panel's default. */
  initialOrderSide?: 'yes' | 'no';
  /** Maker-only when true (post-only mode). Threaded through from the
   *  click handler so the (deprecated) modal flow's postOnly intent
   *  still routes to the panel correctly. */
  postOnly?: boolean;
  /** The game/line key the `prefillPrice` was captured from. When set and it
   *  no longer matches the panel's current `game.key`, the prefill is treated
   *  as STALE and ignored, so the price seeds from the new book's best bid
   *  instead. Used by the value-dashboard spreads/totals tab, where selecting
   *  a different event swaps the panel's game without a fresh price click
   *  (unlike moneyline, whose row-expand resets the intent). Surfaces that
   *  leave this undefined (moneyline, GameDetail) keep the prior behavior. */
  gameKey?: string;
  /** Monotonic stamp for a deliberate price interaction. It lets an exact
   *  repeat click remain a command (re-apply + flash the same price) without
   *  making parent object identity a dependency and re-running on every render. */
  interactionSequence?: number;
}

/** Spread/Total bet context. When provided, the panel hides its away/home
 *  team selector and per-team price strip, renders `title` in the header
 *  with the chip strip directly below it, and uses the `marketSide` YES/NO
 *  toggle as the only side control. The synthesized GameRow's away/home
 *  slots point to the same VenueBook, so the team selector is a no-op for
 *  these bets and the chip strip is the meaningful control. */
export interface BetLineContext {
  /** Side- and chip-aware bet description — e.g. YES "SF Giants wins by
   *  over 2.5 runs" vs NO "Oakland Athletics loses by under 2.5 runs";
   *  Total YES "SF @ LAD Total more than 8.5" vs NO "less than 8.5". The
   *  panel passes its own `panelSide` + `panelChipIdx` so the header
   *  tracks both the YES/NO toggle in OrderEntryForm AND any chip change
   *  inside the panel (which doesn't push back to the dashboard). */
  titleFor: (side: MarketSide, idx: number) => React.ReactNode;
  chips: { key: string; label: string; priceCents: number | null }[];
  /** Dashboard's currently-selected idx. The panel maintains its own
   *  local panelChipIdx initialized from this and synced when this changes
   *  (dashboard → panel). Panel-side chip clicks update local state only
   *  and never call back through `onSelectIdx`. */
  selectedIdx: number;
  /** Reserved for future "push from panel back to dashboard" flows; the
   *  current panel intentionally does NOT call this on chip changes so the
   *  dashboard stays put when the trader is exploring lines. */
  onSelectIdx: (idx: number) => void;
  /** Re-synthesize the GameRow for a different chip idx without touching
   *  the dashboard's selection. Returns null for an out-of-range idx. */
  getGameForIdx: (idx: number) => GameRow | null;
}

interface GameDetailPanelProps {
  game: GameRow;
  /** Close handler — omit when the panel is always-mounted (e.g.
   *  embedded in the GameDetail page's sticky right column). When
   *  absent, the X button isn't rendered. */
  onClose?: () => void;
  tradeIntent?: TradeIntent | null;
  marketSide: MarketSide;
  /** Keeps parent trade intent in sync when the user switches team tabs (row-click toggle uses this). */
  onTeamSideChange?: (side: TeamSide) => void;
  /** Set when the panel is rendering a synthesized spread/total bet — see
   *  BetLineContext. Drives the alternate header layout. */
  betLineContext?: BetLineContext | null;
  /** Unabated consensus prices (fair-prob cents, full-game) for the
   *  per-team summary block's "U" row. Omit on surfaces that don't
   *  subscribe to the SSE odds stream — the row is hidden when both
   *  values are missing. Cents-then-formatted by the panel so callers
   *  just thread the latest snapshot through.
   *
   *  Caller is responsible for aligning awayCents/homeCents to the
   *  panel's awayCode/homeCode columns BEFORE passing them in
   *  (compare unabated.awayTeam vs game.awayCode, swap if needed).
   *  K and P rows render straight off game.kalshi/polymarket.away+home
   *  — they're already self-consistent with the GameRow's column
   *  labels, so applying any cross-row swap to them would break the
   *  alignment that the moneyline value-dashboard view already shows
   *  correctly. */
  unabated?: {
    awayCents?: number | null;
    homeCents?: number | null;
    /** Tenth-of-a-cent fair probabilities (0–1000), already swapped to the
     *  panel's columns like the cents. When present the U row DISPLAYS
     *  these ("88.8%") — the order-entry panel is the one surface that
     *  shows the sub-cent value; everywhere else the integer cents remain
     *  the displayed price. Absent on pre-rollout hydra (U row falls back
     *  to the rounded cents). */
    awayTenths?: number | null;
    homeTenths?: number | null;
    /** Sub-cent trend state for the same consensus bucket the cents came
     *  from (marketStore's `useConsensusTrendFor` on the dashboard, the
     *  odds-stream hub's `consensusTrends` on GameDetail). Drives the U
     *  row's recency-brightness + trend arrows; the panel runs its own
     *  glow ticker so the fades ride down without parent re-renders. */
    trend?: ConsensusTrendState | null;
    /** True when the caller swapped away/home to match the panel columns
     *  — the trend state tracks Unabated's own slots, so the U row must
     *  apply the same flip when reading per-side glow. */
    swapped?: boolean;
    /** Sharp-anchor flag (behavior C). null = unknown (pre-rollout). */
    sharpBookUsed?: boolean | null;
  } | null;
  /** OddsPapi de-vigged fair values for the "O" row directly below the U
   *  row (D4-amended). Like `unabated`, caller-aligned: build via
   *  lib/opOdds.buildOpFairView — the ONE fair-view/swap helper — before
   *  passing in; away/home are already the panel's columns. Null/omitted
   *  hides the row entirely (old hydra, provider off, unmatched game,
   *  showOpOdds off, or a frame with neither consensus nor anchor fair) —
   *  the panel is pixel-identical to pre-feature then. Hydra-computed
   *  fair values (rule 6c); independent of the YES/NO toggle like U. */
  opOdds?: OpFairView | null;
  /** OP consensus trend bucket for the O row's velocity arrows + recency
   *  brightness (marketStore `useOpTrendFor`; OWN feed, never Unabated's). */
  opOddsTrend?: ConsensusTrendState | null;
}

/**
 * Tooltip for the ticket's Price row (#535).
 *
 * The row can show two prices, and only one of them is going on the wire. A
 * trader reading `25¢ → 26.32¢` next to a price input that says `25` needs
 * that stated, not inferred — so this names the placed price explicitly and
 * attributes the difference to a fee with a side.
 *
 * The taker branch also says WHY the side is taker. The perspective is read
 * from the book, not from the order type, so a resting-looking price can be
 * taker whenever it crosses — routinely when the trader types through the
 * spread, and on a locked book (bid == ask) even for a price tapped from a BID
 * row, where the ladder row's own label still reads maker. The panel is right
 * there (a buy at a locked price does cross and does pay the taker rate), but
 * "right and unexplained" is how a trader loses trust in a ticket.
 *
 * Undefined when the two prices agree: Raw mode, Polymarket, a flat or
 * unloaded schedule, or a zero applicable rate. There is nothing to reconcile
 * then, and a tooltip on an unremarkable row is noise.
 */
function priceQuoteTitle(quote: OrderTicketQuote): string | undefined {
  const feeCents = roundToCentiCents(quote.effectiveCents - quote.rawCents);
  if (feeCents === 0) return undefined;
  const because = quote.perspective === 'taker' ? ' — crosses the book' : '';
  return (
    `Placing ${quote.rawCents}¢${because} — odds use ${quote.effectiveCents}¢, ` +
    `the all-in price after the ${feeCents}¢ ${quote.perspective} fee`
  );
}

// ── Order Entry Form ────────────────────────────────────────────

// Stable, monotonic ids for ladder rungs (avoids Math.random / index keys so
// React reconciles rung inputs correctly as rows are added / removed).
let ladderLegSeq = 0;
const nextLadderLegId = () => `leg-${(ladderLegSeq += 1)}`;

export function OrderEntryForm({
  game,
  selectedSide,
  venue,
  orderSide,
  onOrderSideChange,
  price,
  onPriceChange,
  orderType,
  onOrderTypeChange,
  priceFlashKey,
  extraLegs,
  onAddLeg,
  onUpdateLeg,
  onRemoveLeg,
  onClearLegs,
  oscarReleaseIntent,
  onClearOscarReleaseIntent,
  receiptsHost,
}: {
  game: OrderEntryTarget;
  selectedSide: TeamSide;
  /** Which venue this ticket targets. Drives both the book the form
   *  reads its ticker/tokens from AND which order-placement path
   *  `useOrderEntryModel`'s `submit()` dispatches to (accountStore.placeOrder
   *  for Kalshi, placePolyOrderViaRelay for Polymarket). */
  venue: 'kalshi' | 'polymarket';
  /** Controlled by GameDetailPanel — same value drives the orderbook ladders
   *  below, so toggling here ripples down but never up to the moneylines
   *  table (which holds its own independent marketSide). */
  orderSide: MarketSide;
  onOrderSideChange: (next: MarketSide) => void;
  /** Controlled price string — clicking a bid level in the orderbook
   *  below populates this; market orders ignore it. */
  price: string;
  onPriceChange: (next: string) => void;
  /** Increments on every external write to `price` (orderbook click,
   *  tradeIntent prefill, etc.) so the input can play a brief glow
   *  animation. User typing does NOT bump this. */
  priceFlashKey: number;
  /** 'limit' (post a resting bid at `price`) vs 'market' (take whatever's
   *  on the ask). Clicking an ask level flips this to 'market'. */
  orderType: 'limit' | 'market';
  onOrderTypeChange: (next: 'limit' | 'market') => void;
  /** Extra ladder rungs beyond the base price/qty inputs (Kalshi limit-only).
   *  Empty === single-order mode. State + mutators live in GameDetailPanel so
   *  orderbook clicks can append rungs. */
  extraLegs: LadderLeg[];
  onAddLeg: (seed?: { price?: string; qty?: string }) => void;
  onUpdateLeg: (id: string, patch: Partial<Pick<LadderLeg, 'price' | 'qty' | 'team'>>) => void;
  onRemoveLeg: (id: string) => void;
  onClearLegs: () => void;
  /** Armed release-and-overtake (from a blocker row in GameOpenOrders):
   *  cancel this order, take its protected level, then optionally attach a
   *  replacement blocker — one relay round-trip. Lifted to the shell (sibling
   *  arms it). null = not armed. */
  oscarReleaseIntent: OscarReleasePrefill | null;
  onClearOscarReleaseIntent: () => void;
  /** #667 — where the durable receipt cards render. Three states, and the
   *  distinction between the last two is what keeps OscarTicketHarness (which
   *  mounts this form alone) byte-identical:
   *    undefined — prop omitted: render inline, exactly as before #667;
   *    null      — a host was promised but has not mounted yet: render nothing;
   *    element   — portal the cards into the panel's host, below the book.
   *  The receipt DATA never moves: useOrderEntryModel still owns both fetches,
   *  the ack-seq refresh, and the fire path's loadExactOscarReceipt. */
  receiptsHost?: HTMLDivElement | null;
}) {
  const user = useAuthStore((s) => s.user);
  const {
    isKalshiVenue,
    ticker,
    teamCode,
    accountConnected,
    hasTicker,
    hasPolyTokens,
    priceBounds,
    previousPrice,
    nextPrice,
    bestYesHint,
    bestNoHint,
    priceFlashing,
    priceCents,
    qty,
    minQty,
    count,
    defaultContracts,
    quickSizes,
    expiryMode,
    gtsAvailable,
    customExpiry,
    feeLabel,
    feeTitle,
    totalCostLabel,
    maxPayoutLabel,
    maxProfit,
    maxProfitLabel,
    effectiveOddsLabel,
    priceQuote,
    effectiveBreach,
    marketStale,
    submitDisabled,
    submitting,
    ladderActive,
    ladderSummary,
    ladderTeamSplit,
    ladderTotals,
    oscarAccess,
    oscarActive,
    oscarReleaseArmed,
    blockerOn,
    blockerQty,
    oscarPreview,
    showOscarReceipt,
    showOneClickReceipts,
    oscarReceipt,
    oscarReceiptLoading,
    oscarReceiptError,
    refreshOscarReceipt,
    oneClickReceipts,
    oneClickReceiptsLoading,
    oneClickReceiptsError,
    refreshOneClickReceipts,
    result,
    clearResult,
    setQty,
    setCustomExpiry,
    setExpiryMode,
    setBlockerOn,
    setBlockerQty,
    enterOscarMode,
    exitOscarMode,
    applyPriceStep,
    applyOrderSide,
    submit,
    fireOscar,
  } = useOrderEntryModel({
    game,
    selectedSide,
    venue,
    orderSide,
    price,
    orderType,
    priceFlashKey,
    extraLegs,
    oscarReleaseIntent,
    onPriceChange,
    onOrderSideChange,
    onClearLegs,
    onClearOscarReleaseIntent,
    onRemoveLeg,
  });

  const oscarSubmissionTakePriceCents = oscarPreview.ok
    ? (oscarPreview.legs.find((leg) => leg.role === 'take')?.priceCents ?? null)
    : null;
  const oscarSubmissionTakeQuantity = oscarPreview.ok
    ? (oscarPreview.legs.find((leg) => leg.role === 'take')?.qty ?? null)
    : null;
  const gtsExpiryBlocked =
    orderType === 'limit' && expiryMode === 'gts' && !gtsAvailable;

  // #594 — the chip strip is a menu of the sizes firable AT THIS VENUE, so it
  // is keyed on the applied quantity rather than on the stored preset list.
  const quickSizeChips = useMemo(
    () => buildQuickSizeChips(quickSizes, minQty, isKalshiVenue ? 'Kalshi' : 'Polymarket'),
    [quickSizes, minQty, isKalshiVenue],
  );

  // #472 pre-fire warning — the SAME registry query the relay uses to derive
  // kill legs, so the preview names exactly what one click would kill
  // ("KILL <name>'s blocker @ x¢") or, while kill mode is off, warns that a
  // friendly blocker will stop the take via STP. Relay-supplied ownership.
  const oscarKillPreview = useOscarKillPreview({
    enabled: oscarActive && oscarPreview.ok,
    scopeKey: user ? `${user.id}:${user.orgId ?? ''}` : null,
    ticker: ticker || null,
    side: orderSide,
    priceCents: oscarSubmissionTakePriceCents,
    quantity: oscarSubmissionTakeQuantity,
  });

  // #481 — how much this event's state can be believed. Presentation only:
  // it is NOT in `submitDisabled` and NOT in the oscar fire button's disabled
  // expression, deliberately. Refusing to place while the state is uncertain
  // is the fail-closed autonomous policy, which #469 keeps.
  const eventStateCertainty = useEventStateCertainty(
    game.gameState ?? 'pre',
    game.gameStateAuthority,
    game.key,
  );

  // #667 — built here, ABOVE the first early return, because the
  // Kalshi-disconnected branch below consumes them too. Declaring them at the
  // natural site near the main return would reference them before init.
  const receiptCards =
    showOscarReceipt || showOneClickReceipts ? (
      <>
        {/* #302 — durable Oscar receipt. Independent of `oscarActive`: it must
            still be here after leaving Overtake mode, after a refresh, and
            after reopening the game. The transient `result` banner is this
            fire's HTTP outcome; THIS card is what the audit recorded. It is
            also a RELAY read over durable audit rows, not a venue read, so a
            disconnected Kalshi account is no reason to hide it. */}
        {showOscarReceipt && (
          <OscarSequenceReceipt
            receipt={oscarReceipt}
            loading={oscarReceiptLoading}
            error={oscarReceiptError}
            onRefresh={() => void refreshOscarReceipt()}
          />
        )}
        {/* #314 — durable one-click receipts, on the same reasoning:
            independent of `armed`, and a relay read that survives a
            disconnected account. */}
        {showOneClickReceipts && (
          <OneClickReceipts
            receipts={oneClickReceipts}
            loading={oneClickReceiptsLoading}
            error={oneClickReceiptsError}
            onRefresh={() => void refreshOneClickReceipts()}
          />
        )}
      </>
    ) : null;
  const placedReceipts =
    receiptsHost === undefined
      ? receiptCards
      : receiptsHost
        ? createPortal(receiptCards, receiptsHost)
        : null;

  // Kalshi orders need an active Kalshi connection; Poly orders flow
  // through the relay which has its own connect status (described in
  // the error message if it's down). Only gate on the Kalshi-connected
  // status when this ticket is targeting Kalshi.
  if (isKalshiVenue && !accountConnected) {
    return (
      <div className="border-border space-y-2 border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">Connect your Kalshi account in Settings to trade</p>
        {/* #302/#314 — trading controls stay hidden; recovery evidence does
            not. Placement follows the same depth rule as the connected path:
            a disconnected ACCOUNT does not imply a disconnected BOOK, and the
            live ladder below is still worth the top of the rail. */}
        {placedReceipts}
      </div>
    );
  }

  if (!hasTicker) {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">
          No {isKalshiVenue ? 'Kalshi' : 'Polymarket'} market available for this team.
        </p>
      </div>
    );
  }

  if (!isKalshiVenue && !hasPolyTokens) {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">
          This Polymarket cell has no outcome tokens attached yet — open orders + cancellation
          still work from Settings / Positions, but placement isn't wired here.
        </p>
      </div>
    );
  }

  return (
    // Overtake tightens the block gaps: the Oscar surface stacks a banner,
    // preview and warning above the book, and at w-96 the loose rhythm alone
    // cost the ladder a rows-worth of fold (#667).
    <div className={cn('border-border border-b px-4 py-3', oscarActive ? 'space-y-2' : 'space-y-3')}>
      {/* Above the ticket, not below it: the trader must meet the doubt
          before the price and size fields, not after committing to them.
          Renders nothing when the state is trusted. */}
      {!game.market && <EventStateWarning certainty={eventStateCertainty} />}
      {marketStale && (
        <div className="rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
          No update from this market in over one game window. Its price is not
          current, so trading is unavailable until the feed resumes.
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">Order Entry</span>
        <span className="text-muted-foreground/50 font-mono text-[10px]">{ticker.length > 30 ? `...${ticker.slice(-28)}` : ticker}</span>
      </div>

      {/* Side toggle */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => applyOrderSide('yes')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'yes'
                ? 'bg-positive text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY YES
          </button>
          <button
            type="button"
            onClick={() => applyOrderSide('no')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'no'
                ? 'bg-negative text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY NO
          </button>
        </div>
        <p className="text-muted-foreground break-words text-center text-[11px] [overflow-wrap:anywhere]">
          {game.market ? `${orderSide.toUpperCase()} · ${game.market.title}` : outcomeSideLabel(game, selectedSide, orderSide, venue)}
        </p>
      </div>

      {/* Limit / Market toggle — clicking a bid level in the orderbook below
          flips this to LIMIT, clicking an ask flips it to MARKET. User can
          also toggle manually. */}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => { onOrderTypeChange('limit'); clearResult(); }}
          className={cn(
            'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
            orderType === 'limit'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          LIMIT
        </button>
        {!game.market && <button
          type="button"
          onClick={() => { onOrderTypeChange('market'); clearResult(); }}
          className={cn(
            'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
            orderType === 'market'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          MARKET
        </button>}
      </div>

      {/* Oscar overtake mode (permissioned; Kalshi + limit only). ORDER is
          the everyday ticket; OVERTAKE adds the opposite-side blocker leg
          and fires both through the relay's grouped sequence. */}
      {oscarAccess && isKalshiVenue && orderType === 'limit' && (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => { if (oscarActive) exitOscarMode(); }}
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              !oscarActive
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            ORDER
          </button>
          <button
            type="button"
            onClick={() => { if (!oscarActive) enterOscarMode(); }}
            title="Overtake a level: take/post it and place the corresponding 99−price blocker in one grouped action"
            className={cn(
              'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
              oscarActive
                ? 'bg-amber-500 text-black'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            OVERTAKE
          </button>
        </div>
      )}

      {/* Good to Start / Good to Close / Custom — sits directly under
          LIMIT/MARKET per template. Whole row greys out for MARKET
          orders since they fill immediately and expirationTime is
          meaningless. GTS additionally greys out when the game has no
          known startTimeMs or has already started. CUSTOM reveals a
          datetime-local input below the row. */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          {(['gts', 'gtc', 'custom'] as const).map((mode) => {
            const isMarket = orderType === 'market';
            // Both venues honor expiry: Kalshi via expirationTime (ISO),
            // Polymarket via OrderType.GTD + UserOrderV2.expiration (unix
            // seconds). GTS button still greys out when the game has no
            // scheduled start or has already started — that's a data
            // condition, not a venue capability.
            const disabled =
              isMarket || (mode === 'gts' && !gtsAvailable);
            return (
              <button
                key={mode}
                type="button"
                onClick={() => { if (!disabled) { setExpiryMode(mode); clearResult(); } }}
                disabled={disabled}
                title={
                  isMarket
                    ? 'Market orders fill immediately — expiry n/a'
                    : mode === 'gts'
                      ? gtsAvailable
                        ? 'Good to Start — expires at the event start as it stands now. The venue fixes that time when you place; a later reschedule does not move it.'
                        : 'Good to Start unavailable: game has no scheduled start or already started'
                      : mode === 'gtc'
                        ? 'Good to Close — rests until you cancel or the market settles'
                        : 'Custom — pick a specific date/time for auto-cancel'
                }
                className={cn(
                  'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
                  expiryMode === mode && !isMarket
                    ? mode === 'gts' && !gtsAvailable
                      ? 'bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/40 cursor-not-allowed'
                      : 'bg-primary text-primary-foreground'
                    : disabled
                      ? 'bg-muted/40 text-muted-foreground/40 cursor-not-allowed'
                      : 'bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {mode === 'gts' ? 'GOOD TO START' : mode === 'gtc' ? 'GOOD TO CLOSE' : 'CUSTOM'}
              </button>
            );
          })}
        </div>
        {orderType === 'limit' && expiryMode === 'custom' && (
          <input
            type="datetime-local"
            value={customExpiry}
            onChange={(e) => { setCustomExpiry(e.target.value); clearResult(); }}
            className="border-border bg-input text-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-[11px] focus:outline-none"
          />
        )}
        {orderType === 'limit' && expiryMode === 'gts' && !gtsAvailable && (
          <p role="alert" className="text-amber-300 text-[11px] leading-snug">
            Good to Start is unavailable because a trustworthy future event start is not
            available. Choose Good to Close explicitly to place without an expiry.
          </p>
        )}
      </div>

      {/* Price + Qty */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-[10px]">
            Price (¢){orderType === 'market' ? ' — n/a' : ''}
            {isKalshiVenue && !game.market ? ` · ${teamCode(selectedSide)}` : ''}
          </label>
          <div className="border-border bg-input flex rounded-md border">
            <button
              type="button"
              aria-label="Previous valid price"
              onClick={() => applyPriceStep(previousPrice)}
              disabled={orderType === 'market' || previousPrice === null}
              className="text-muted-foreground hover:text-foreground flex w-7 items-center justify-center disabled:opacity-30"
            >
              <Minus size={12} />
            </button>
            <input
              type="text"
              inputMode="decimal"
              aria-label={game.market ? 'Limit price (cents)' : undefined}
              aria-valuemin={priceBounds.min}
              aria-valuemax={priceBounds.max}
              disabled={orderType === 'market'}
              value={orderType === 'market' ? '' : price}
              onChange={(e) => { onPriceChange(e.target.value); clearResult(); }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  applyPriceStep(previousPrice);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  applyPriceStep(nextPrice);
                }
              }}
              placeholder={
                game.market ? 'Enter price' : orderType === 'market'
                  ? 'best ask'
                  : (orderSide === 'yes' ? bestYesHint : bestNoHint) !== undefined
                    ? String(orderSide === 'yes' ? bestYesHint : bestNoHint)
                    : '50'
              }
              className={cn(
                'text-foreground placeholder-muted-foreground focus:border-primary min-w-0 flex-1 border-x border-y-0 border-border bg-transparent px-2 py-1.5 text-center font-mono text-xs focus:outline-none disabled:opacity-50',
                priceFlashing && 'animate-price-flash',
              )}
            />
            <button
              type="button"
              aria-label="Next valid price"
              onClick={() => applyPriceStep(nextPrice)}
              disabled={orderType === 'market' || nextPrice === null}
              className="text-muted-foreground hover:text-foreground flex w-7 items-center justify-center disabled:opacity-30"
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-muted-foreground text-[10px]">Qty</label>
            {/* Explicit one-tap "apply user default" — overrides whatever
                is in Qty regardless of current value. Distinct from the
                auto-fill on external price writes (which only fires
                when Qty is blank). Kept here, beside the label: it is ONE
                short control, and the row has room for it at every size. */}
            <button
              type="button"
              onClick={() => { setQty(String(Math.max(defaultContracts, minQty))); clearResult(); }}
              title={`Set Qty to your default (${Math.max(defaultContracts, minQty)}) — change in Settings → Preferences${isKalshiVenue ? '' : ` (Polymarket floors it at ${POLY_MIN_ORDER_CONTRACTS})`}`}
              className="text-muted-foreground hover:text-foreground shrink-0 text-[10px] font-medium leading-none"
            >
              + {Math.max(defaultContracts, minQty)}
            </button>
          </div>
          <input
            type="number"
            aria-label={game.market ? 'Contracts' : undefined}
            min={minQty}
            value={qty}
            onChange={(e) => { setQty(e.target.value); clearResult(); }}
            onBlur={() => {
              // Polymarket floor: a typed sub-minimum snaps up on blur (the
              // spinner already can't go below minQty; this catches typing).
              if (isKalshiVenue) return;
              setQty((prev) => {
                const n = parseInt(prev, 10);
                return prev !== '' && Number.isFinite(n) && n < POLY_MIN_ORDER_CONTRACTS
                  ? String(POLY_MIN_ORDER_CONTRACTS)
                  : prev;
              });
            }}
            placeholder="0"
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </div>
        {/* Quick sizes (Settings → Preferences). One tap replaces Qty, same
            override semantics as the apply-default button above.

            Spans BOTH grid columns deliberately. Beside the Qty label the
            strip lived in a 172px cell (rail 384 − px-4 32 = 352, halved by
            grid-cols-2 gap-2), which three presets overflow as soon as one
            reaches four digits — 100/500/1000 already needs 177px, and the
            measured result was the whole rail scrolling 10px horizontally.
            Full width gives the strip 352px, so it holds six-figure sizes
            with room left, keeps Price and Qty aligned, and keeps the row's
            height independent of how long the trader's presets are.

            One chip per DISTINCT APPLIED size, not per stored preset: see
            quickSizeChips.ts for why the venue floor makes those different
            lists. Renders nothing at all when no presets are set, so the
            row costs no vertical space in the default configuration.

            In Overtake the row yields entirely — presets are a convenience,
            and the Oscar surface must leave the book a viewport (#667). */}
        {quickSizeChips.length > 0 && !oscarActive && (
          <div
            data-quick-size-row
            className="col-span-2 flex items-center gap-1.5"
          >
            <span className="text-muted-foreground mr-auto text-[10px]">Quick</span>
            {quickSizeChips.map((chip) => (
              <button
                key={chip.applied}
                data-quick-size-chip
                type="button"
                onClick={() => { setQty(String(chip.applied)); clearResult(); }}
                title={chip.title}
                className="bg-muted text-muted-foreground hover:text-foreground rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none transition-colors"
              >
                {chip.applied}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Oscar overtake surface: blocker toggle + size, armed-release banner,
          and the live leg preview. The preview IS the confirmation — one
          click fires (no modal in the hot path, per the trader spec). */}
      {oscarActive && (
          <div className="space-y-1">
            {oscarReleaseArmed && (
              // One line, not a paragraph: the full explanation lives in the
              // title. The banner sits above the book and every extra line is
              // a book row lost at 1920×1080 (#667).
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-400"
                title="Fires as one round-trip: cancel the resting blocker first, then submit the take. A replacement blocker is added only when BLOCKER is ON."
              >
                Release armed: cancels your {oscarReleaseIntent!.takeSide === 'yes' ? 'NO' : 'YES'}-side
                blocker{' '}
                {oscarSubmissionTakePriceCents == null ? (
                  <>— take price invalid, nothing submits.</>
                ) : oscarSubmissionTakePriceCents === oscarReleaseIntent!.takePriceCents ? (
                  <>
                    → {oscarSubmissionTakePriceCents}¢
                    {blockerOn ? ' → new blocker.' : '; no replacement blocker.'}
                  </>
                ) : (
                  <>
                    → {oscarSubmissionTakePriceCents}¢ (from {oscarReleaseIntent!.takePriceCents}¢ source)
                    {blockerOn ? ' → new blocker.' : '; no replacement blocker.'}
                  </>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setBlockerOn((v) => !v); clearResult(); }}
                title="Attach a new opposite-side blocker (post-only bid at 99 − take price) in the same grouped action"
                className={cn(
                  'rounded-md px-2 py-1 text-[10px] font-semibold transition-colors',
                  blockerOn
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {blockerOn ? 'BLOCKER ON' : 'BLOCKER OFF'}
              </button>
              {blockerOn && (
                <>
                  <input
                    type="number"
                    min={1}
                    value={blockerQty}
                    onChange={(e) => { setBlockerQty(e.target.value); clearResult(); }}
                    placeholder="blocker qty"
                    aria-label="Blocker qty"
                    className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-24 rounded-md border px-2.5 py-1 font-mono text-xs focus:outline-none"
                  />
                  <span className="text-muted-foreground text-[10px]">
                    {oscarPreview.ok && oscarPreview.blockerPriceCents != null
                      ? `blocker @ ${oscarPreview.blockerPriceCents}¢ ${orderSide === 'yes' ? 'NO' : 'YES'} (99 − take)`
                      : 'blocker price = 99 − take'}
                  </span>
                </>
              )}
            </div>
            {/* Live sequence preview — exactly what one click fires, in order. */}
            <div className="border-border rounded-md border px-2.5 py-1">
              {oscarPreview.ok ? (
                <ol className="space-y-0.5">
                  {oscarPreview.legs.map((leg, i) => (
                    <li key={leg.role} className="text-foreground flex items-center gap-2 font-mono text-[11px]">
                      <span className="text-muted-foreground">{i + 1}</span>
                      {leg.role === 'cancel' ? (
                        <span>CANCEL blocker {leg.priceCents}¢ {leg.side.toUpperCase()}</span>
                      ) : leg.role === 'take' ? (
                        <span>
                          BUY {leg.qty} {leg.side.toUpperCase()} @ {leg.priceCents}¢
                          <span className="text-muted-foreground"> · marketable, rests remainder</span>
                        </span>
                      ) : (
                        <span>
                          BUY {leg.qty} {leg.side.toUpperCase()} @ {leg.priceCents}¢
                          <span className="text-muted-foreground"> · blocker, post-only</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <span className="text-muted-foreground text-[11px]">{oscarPreview.error}</span>
              )}
              {oscarKillPreview != null && oscarKillPreview.blockers.length > 0 && (
                <div className="border-border mt-1 space-y-0.5 border-t pt-1">
                  {oscarKillPreview.blockers.map((b, i) => (
                    <div
                      key={`${b.own ? 'self' : 'teammate'}-${b.blockerPriceCents}-${i}`}
                      className="flex items-center gap-2 font-mono text-[11px] text-amber-400"
                    >
                      {oscarKillPreview.killMode === 'on' ? (
                        <span>
                          KILL {b.own ? 'your' : "a teammate's"} blocker @ {b.blockerPriceCents}¢ ×{b.count}
                        </span>
                      ) : (
                        <span>
                          ⚠ {b.own ? 'your' : "a teammate's"} blocker @ {b.blockerPriceCents}¢ ×{b.count} is in the path — the take may be stopped (STP)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* In Overtake the four order figures live HERE, inside the one
                  card — a second stacked bordered card cost the book its
                  viewport at 1920×1080 (#667). Same labels, testids and
                  gating as the standalone card the ordinary ticket keeps. */}
              {count > 0 && priceCents > 0 && (
                <div
                  data-testid="oscar-econ-rows"
                  className="border-border mt-0.5 space-y-0.5 border-t pt-0.5 text-[11px]"
                >
                  <div className="flex items-center justify-between" title={priceQuoteTitle(priceQuote)}>
                    <span className="text-muted-foreground">Price</span>
                    <span className="text-foreground font-mono inline-flex items-center gap-1.5">
                      {priceQuote.effectiveCents !== priceQuote.rawCents && (
                        <>
                          <span className="text-muted-foreground" data-testid="price-row-raw">
                            {priceQuote.rawCents}¢
                          </span>
                          <span className="text-muted-foreground/60" aria-hidden>
                            →
                          </span>
                        </>
                      )}
                      <span data-testid="price-row-basis">{priceQuote.effectiveCents}¢</span>
                      <span className="h-3 w-px self-center bg-current opacity-30" />
                      <span className="opacity-70" data-testid="price-row-odds">{priceQuote.odds}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground" title={feeTitle}>
                      Total Cost (fee)
                    </span>
                    <span className="text-foreground font-mono">
                      {totalCostLabel}{' '}
                      <span className="text-muted-foreground">({feeLabel})</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground" title="Gross payout per contract on win — $1 each">
                      Max Payout
                    </span>
                    <span className="text-positive font-mono inline-flex items-center gap-1.5">
                      <span>{maxPayoutLabel}</span>
                      <span className="h-3 w-px self-center bg-current opacity-30" />
                      <span className="opacity-70">{effectiveOddsLabel}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground" title="Max Payout − Total Cost (net of fee)">
                      Max Profit
                    </span>
                    <span
                      className={`font-mono ${
                        maxProfit === null
                          ? 'text-muted-foreground'
                          : maxProfit >= 0
                            ? 'text-positive'
                            : 'text-negative'
                      }`}
                    >
                      {maxProfitLabel}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
      )}

      {/* Multi-price ladder (Kalshi, limit-only). Each rung becomes its own
          resting limit order on the SAME side; "Place N orders" fires them
          paced ≤4/sec (shared Kalshi account bucket). Tapping a bid in the
          book below appends a rung once the ladder is started. No extra rungs
          === the single-order form above, unchanged. */}
      {isKalshiVenue && orderType === 'limit' && !oscarActive && !game.market && (
        <div className="space-y-1.5">
          {/* Rung list is capped + scrolls internally so a long ladder can't
              outgrow the panel — in pinned-ticket mode the panel itself doesn't
              scroll, so an unbounded list would clip the summary / Place button
              and starve the orderbook region below. Add/Clear + Place stay put. */}
          {extraLegs.length > 0 && (
            <div className="max-h-56 space-y-1.5 overflow-y-auto pr-0.5">
          {extraLegs.map((leg) => {
            const check = validateLeg({ price: leg.price, qty: leg.qty });
            const invalid = !check.ok && (leg.price !== '' || leg.qty !== '');
            return (
              <div key={leg.id} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2">
                {/* Team toggle — which team's market this rung is placed on.
                    Side (YES/NO) stays global; only the team varies per rung,
                    so a market is never quoted YES and NO at once. */}
                <button
                  type="button"
                  onClick={() => { onUpdateLeg(leg.id, { team: leg.team === 'away' ? 'home' : 'away' }); clearResult(); }}
                  title={`Placing on ${leg.team === 'away' ? game.awayCode : game.homeCode} — tap to switch team`}
                  className="bg-muted text-foreground hover:bg-muted/70 min-w-10 rounded-md px-2 font-mono text-[11px] font-semibold"
                >
                  {leg.team === 'away' ? game.awayCode : game.homeCode}
                </button>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={leg.price}
                  onChange={(e) => { onUpdateLeg(leg.id, { price: e.target.value }); clearResult(); }}
                  placeholder="¢"
                  aria-label="Ladder price (¢)"
                  className={cn(
                    'border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none',
                    invalid && 'border-red-500/60',
                  )}
                />
                <input
                  type="number"
                  min={1}
                  value={leg.qty}
                  onChange={(e) => { onUpdateLeg(leg.id, { qty: e.target.value }); clearResult(); }}
                  placeholder="qty"
                  aria-label="Ladder qty"
                  className={cn(
                    'border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none',
                    invalid && 'border-red-500/60',
                  )}
                />
                <button
                  type="button"
                  onClick={() => { onRemoveLeg(leg.id); clearResult(); }}
                  title="Remove this price level"
                  className="text-muted-foreground hover:text-foreground bg-muted flex items-center justify-center rounded-md px-2"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
            </div>
          )}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => { onAddLeg(); clearResult(); }}
              disabled={extraLegs.length >= LADDER_MAX_LEGS}
              title={
                extraLegs.length >= LADDER_MAX_LEGS
                  ? `Ladder is capped at ${LADDER_MAX_LEGS} price levels`
                  : 'Add another price level — all placed together on one click'
              }
              className="text-primary hover:text-primary/80 text-[11px] font-semibold disabled:opacity-40"
            >
              + Add price level
            </button>
            {extraLegs.length > 0 && (
              <button
                type="button"
                onClick={() => { onClearLegs(); clearResult(); }}
                className="text-muted-foreground hover:text-foreground text-[10px]"
              >
                Clear ladder
              </button>
            )}
          </div>
        </div>
      )}

      {/* Limit-breach preview (advisory; the relay is the authoritative gate)
          When effectiveBreach is non-null, the order would exceed one of the
          trader's caps. We disable the submit button below as the soft gate
          + structured error rendering covers the case where the relay
          rejects despite stale local state. */}
      {effectiveBreach && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-400">
          {effectiveBreach.rule === 'LIMIT_MAX_CONTRACTS'
            ? `Would exceed your contract cap: ${effectiveBreach.current.toLocaleString()} + ${effectiveBreach.requested.toLocaleString()} > ${effectiveBreach.cap.toLocaleString()}`
            : `Would exceed your position cap: ${effectiveBreach.current.toLocaleString()} + 1 > ${effectiveBreach.cap.toLocaleString()}`}
        </div>
      )}

      {/* Venue caveat — Polymarket mechanics that differ from Kalshi, kept
          to one muted line so it informs without interrupting the flow. */}
      {!isKalshiVenue && (
        <p className="text-muted-foreground text-[10px] leading-snug">
          Polymarket: 5-contract minimum · limit orders only · exit by selling
          your tokens (Positions → Close) · marketable limits include the
          current taker-fee estimate.
        </p>
      )}

      {/* Price / Total Cost (fee) / Max Payout / Max Profit — Kalshi fees use
          hydra's series schedule; Poly fees use the relay's condition-id
          schedule and distinguish resting from crossing limits. Max Payout
          is gross qty × $1; Max Profit is net of the estimated fee.
          Maker fees only apply on fill — a resting limit that never fills
          costs nothing. Same four-field shape as TradeModal + the SELL
          modal so the trader sees the same layout everywhere. Outlined
          card matches the framing the GameDetail TradeTicketPanel used
          to give this summary block a visual home. */}
      {/* Gated on there BEING a price. In market mode the input is blank by
          design but the `price` state survives the toggle, so this card used
          to render four figures off a number that would never be sent — three
          of them price-derived (Price, Total Cost, Max Profit) and the fourth,
          Max Payout, honest in dollars but carrying phantom all-in odds. #535
          made that louder by putting a fee basis on it, which forced the
          question: the card's own contract is to describe the order about to
          be placed, and a market order has no price to describe. Showing
          nothing is the correct null. What a market ticket SHOULD quote (the
          book's best ask? a depth-weighted estimate?) is a live design
          question and its own issue.

          In Overtake these rows render inside the sequence-preview card
          instead — one bordered card, not two stacked (#667). */}
      {!oscarActive && !ladderActive && orderType !== 'market' && count > 0 && priceCents > 0 && (
        <div className="border-border space-y-1 rounded-lg border px-3 py-2 text-sm">
          {/* Price / odds on ONE basis (#452's contract, #535 for this ticket).
              The odds always come from the RIGHT-hand cents figure — the raw
              price and arrow are prefixed only when a fee moves the basis, so
              Raw mode renders exactly as it always has. Muting the raw half
              keeps the pairing unambiguous: a reviewer must be able to see at
              a glance which number the odds belong to. */}
          <div className="flex items-center justify-between" title={priceQuoteTitle(priceQuote)}>
            <span className="text-muted-foreground">Price</span>
            <span className="text-foreground font-mono inline-flex items-center gap-1.5">
              {priceQuote.effectiveCents !== priceQuote.rawCents && (
                <>
                  <span className="text-muted-foreground" data-testid="price-row-raw">
                    {priceQuote.rawCents}¢
                  </span>
                  <span className="text-muted-foreground/60" aria-hidden>
                    →
                  </span>
                </>
              )}
              <span data-testid="price-row-basis">{priceQuote.effectiveCents}¢</span>
              <span className="h-3 w-px self-center bg-current opacity-30" />
              <span className="opacity-70" data-testid="price-row-odds">{priceQuote.odds}</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span
              className="text-muted-foreground"
              title={feeTitle}
            >
              Total Cost (fee)
            </span>
            <span className="text-foreground font-mono">
              {totalCostLabel}{' '}
              <span className="text-muted-foreground">({feeLabel})</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Gross payout per contract on win — $1 each">
              Max Payout
            </span>
            <span className="text-positive font-mono inline-flex items-center gap-1.5">
              <span>{maxPayoutLabel}</span>
              <span className="h-3 w-px self-center bg-current opacity-30" />
              <span className="opacity-70">{effectiveOddsLabel}</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Max Payout − Total Cost (net of fee)">
              Max Profit
            </span>
            <span
              className={`font-mono ${
                maxProfit === null
                  ? 'text-muted-foreground'
                  : maxProfit >= 0
                    ? 'text-positive'
                    : 'text-negative'
              }`}
            >
              {maxProfitLabel}
            </span>
          </div>
        </div>
      )}

      {/* Ladder aggregate — totals across all rungs (fee model matches the
          single-order card, summed). Shown in place of the per-order card. */}
      {ladderActive && ladderTotals && ladderTotals.contracts > 0 && (
        <div className="border-border space-y-1 rounded-lg border px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Ladder</span>
            <span className="text-foreground font-mono">
              {ladderTotals.legs} order{ladderTotals.legs === 1 ? '' : 's'} · {ladderTotals.contracts} contracts
            </span>
          </div>
          {ladderTeamSplit && ladderTeamSplit.away > 0 && ladderTeamSplit.home > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">By team</span>
              <span className="text-foreground font-mono">
                {game.awayCode} {ladderTeamSplit.away}ct · {game.homeCode} {ladderTeamSplit.home}ct
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Contracts-weighted average price across all rungs">
              Avg Price
            </span>
            <span className="text-foreground font-mono">{Math.round(ladderTotals.avgPriceCents)}¢</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Σ (price × qty) across rungs, plus estimated maker fee">
              Total Cost (fee)
            </span>
            <span className="text-foreground font-mono">
              {ladderTotals.totalCostDollars === null ? '—' : formatMoney(ladderTotals.totalCostDollars)}{' '}
              <span className="text-muted-foreground">
                ({ladderTotals.netFeeDollars === null ? '—' : formatMoney(ladderTotals.netFeeDollars)})
              </span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Gross payout if every rung wins — $1 per contract">
              Max Payout
            </span>
            <span className="text-positive font-mono">{formatMoney(ladderTotals.maxPayoutDollars)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Max Payout − Total Cost (net of fee)">
              Max Profit
            </span>
            <span
              className={`font-mono ${
                ladderTotals.maxProfitDollars === null
                  ? 'text-muted-foreground'
                  : ladderTotals.maxProfitDollars >= 0
                    ? 'text-positive'
                    : 'text-negative'
              }`}
            >
              {ladderTotals.maxProfitDollars === null
                ? '—'
                : formatMoney(ladderTotals.maxProfitDollars, { showSign: true })}
            </span>
          </div>
        </div>
      )}

      {/* Keep the economics above the action. The trader must see the
          fee-adjusted cost before they can reach Place Order. In oscar mode
          this is the DEDICATED fire button (amber, distinct from the everyday
          Place Order) — the inline preview above is the confirmation; one
          click executes the whole sequence. */}
      {oscarActive ? (
        <button
          onClick={() => { void fireOscar(); }}
          disabled={
            submitting || marketStale || gtsExpiryBlocked || !!effectiveBreach || !qty || !price
          }
          className="flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 py-2 text-xs font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
        >
          {submitting && <Loader2 size={12} className="animate-spin" />}
          {submitting
            ? 'Firing...'
            : oscarReleaseArmed
              ? 'Release & Overtake'
              : blockerOn
                ? 'Fire Overtake + Blocker'
                : 'Fire Overtake'}
        </button>
      ) : (
        <button
          onClick={() => { void submit(); }}
          disabled={submitDisabled || gtsExpiryBlocked}
          className="bg-primary text-primary-foreground hover:bg-primary/90 flex w-full items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold transition-colors disabled:opacity-50"
        >
          {submitting && <Loader2 size={12} className="animate-spin" />}
          {submitting
            ? 'Placing...'
            : ladderActive
              ? `Place ${ladderSummary?.legs ?? 0} order${(ladderSummary?.legs ?? 0) === 1 ? '' : 's'}`
              : 'Place Order'}
        </button>
      )}

      {/* Result — #474: one typed banner for every outcome this ticket can
          produce (plain Kalshi/Polymarket placements and Oscar sequences),
          so severity and the screen-reader announcement live in one place. */}
      <OrderResultBanner result={result} />

      {placedReceipts}
    </div>
  );
}

// ── Game Detail Panel ───────────────────────────────────────────

export function GameDetailPanel({ game: gameProp, onClose, tradeIntent, marketSide, onTeamSideChange, betLineContext = null, unabated = null, opOdds = null, opOddsTrend = null }: GameDetailPanelProps) {
  const [selectedSide, setSelectedSide] = useState<TeamSide>(tradeIntent?.side ?? 'away');
  // Panel-local venue (Kalshi vs Polymarket). Seeded from tradeIntent so
  // a Poly pill click on the dashboard opens the ticket on the Poly side.
  // Same primitive-dep pattern as selectedSide so the parent's tradeIntent
  // object-literal churn doesn't stomp local toggles.
  const [panelVenue, setPanelVenue] = useState<'kalshi' | 'polymarket'>(
    tradeIntent?.venue ?? 'kalshi',
  );

  // #667 — the receipt cards render below the book, not inside the ticket box.
  // A callback ref into state (rather than a useRef) so the first commit that
  // mounts the host re-renders the form with a real portal target; a ref would
  // hold the element but never tell the ticket it arrived.
  const [receiptsHost, setReceiptsHost] = useState<HTMLDivElement | null>(null);

  // Panel-local YES/NO state. Initialized from `marketSide` and re-synced
  // whenever the parent changes it (so toggling YES/NO on the moneylines
  // table still cascades into the panel + its ladders). The order-entry
  // form's BUY YES/BUY NO toggle drives this back through onOrderSideChange,
  // which only updates this local state — the parent's marketSide is never
  // pushed up, so the moneylines table stays put.
  //
  // #348 — KEPT AS AN EFFECT, and the reason is worth recording because the
  // panelChipOverride pattern below looks like it should apply here.
  //
  // It does not. That pattern stamps a local override with the authority value
  // it was made against and treats a mismatch as "stale". For `selectedIdx`,
  // which has many values, returning to the exact stamped one is rare. For
  // `marketSide`, which has TWO, any two parent flips return to it — so the
  // override would revive on every second toggle of the dashboard's moneylines
  // control, leaving the panel on NO while the dashboard reads YES. Measured,
  // not assumed: the derived form failed `lets a real parent change override
  // the panel-local click` in GameDetailPanel.panelSync.test.tsx.
  //
  // What a stamp cannot express is "the parent changed since the trader
  // clicked" — that is edge detection. React's adjust-state-during-render
  // pattern (hold the previous prop in state, compare, setState during render)
  // DOES express it. It is unavailable here for the same reason as the reseed
  // effect further down: it fires `react-hooks/set-state-in-render`, which is
  // absent from lint-budget.json and so fails the gate outright.
  const [panelSide, setPanelSide] = useState<MarketSide>(marketSide);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPanelSide(marketSide);
  }, [marketSide]);

  // Panel-side chip "override" — set when the user clicks a chip inside
  // the panel. Tagged with the dashboard's selectedIdx at the moment of
  // the click; if the dashboard later moves, that tag stops matching and
  // we fall back to the dashboard's idx *synchronously* on the same
  // render (no useEffect lag). Same one-way pattern as panelSide above:
  // dashboard → panel sync, but panel clicks don't push back.
  const [panelChipOverride, setPanelChipOverride] = useState<{
    value: number;
    basedOnDashboardIdx: number;
  } | null>(null);

  const panelChipIdx =
    betLineContext &&
    panelChipOverride &&
    panelChipOverride.basedOnDashboardIdx === betLineContext.selectedIdx
      ? panelChipOverride.value
      : (betLineContext?.selectedIdx ?? 0);

  const handlePanelChipSelect = useCallback(
    (idx: number) => {
      if (!betLineContext) return;
      setPanelChipOverride({ value: idx, basedOnDashboardIdx: betLineContext.selectedIdx });
    },
    [betLineContext],
  );

  // Re-synthesize a divergent GameRow when the panel's effective idx
  // differs from the dashboard's (i.e. user clicked inside the panel).
  // When they match, falls through to the prop.
  const game: GameRow = (() => {
    if (!betLineContext) return gameProp;
    if (panelChipIdx === betLineContext.selectedIdx) return gameProp;
    return betLineContext.getGameForIdx(panelChipIdx) ?? gameProp;
  })();
  const canonicalGameState = useCanonicalGameState(game.key);

  // A prefill price goes STALE once the panel's game no longer matches the
  // game the click came from — e.g. the spreads/totals tab swapped the active
  // event without a new price click. When tradeIntent tags a gameKey that
  // doesn't match, ignore the prefill so the price seeds from the new book's
  // best bid. Untagged intents (moneyline, GameDetail) always "match".
  const prefillMatchesGame =
    tradeIntent?.gameKey == null || tradeIntent.gameKey === game.key;

  // Form intent state lifted up so orderbook-row clicks can populate the
  // OrderEntryForm: clicking a bid prefills the price + flips type=limit;
  // clicking an ask flips type=market (price ignored).
  const [formPrice, setFormPrice] = useState(
    () =>
      tradeIntent?.prefillPrice != null && prefillMatchesGame
        ? String(tradeIntent.prefillPrice)
        : '',
  );
  const [formOrderType, setFormOrderType] = useState<'limit' | 'market'>('limit');
  // Bumps on every external write to formPrice. OrderEntryForm watches
  // it to play a one-shot glow on the input. User typing in the input
  // goes through onPriceChange → setFormPrice directly and does NOT
  // bump, so typing never triggers the animation.
  const [priceFlashKey, setPriceFlashKey] = useState(0);
  const writeFormPriceFromExternal = useCallback((next: string) => {
    setFormPrice(next);
    setPriceFlashKey((k) => k + 1);
  }, []);

  // Multi-price ladder (Kalshi, limit-only): extra rungs beyond the base
  // price/qty inputs. Empty === today's single-order behavior. Lifted here
  // (not inside OrderEntryForm) so the orderbook onLevelClick below can append
  // a rung — "once a ladder is started, tapping a bid adds a rung at that
  // price." Default rung qty seeds from the user's default-contracts pref.
  const defaultContractsPref = useDisplaySettings((s) => s.defaultContracts);
  const [extraLegs, setExtraLegs] = useState<LadderLeg[]>([]);
  const addLadderLeg = useCallback(
    (seed?: { price?: string; qty?: string; team?: TeamSide }) => {
      setExtraLegs((legs) => {
        if (legs.length >= LADDER_MAX_LEGS) return legs;
        // Seed the price: a book-tap passes the tapped price; a manual "+ Add"
        // steps 1¢ below the previous rung (or the base price) so the button
        // builds a descending bid ladder instead of an empty, invalid row.
        let price = seed?.price;
        if (price == null) {
          const prevStr = legs.length > 0 ? legs[legs.length - 1].price : formPrice;
          const prev = parseInt(prevStr, 10);
          price = Number.isFinite(prev) ? String(Math.max(1, prev - 1)) : '';
        }
        return [
          ...legs,
          {
            id: nextLadderLegId(),
            // Default to the active team; each rung can be moved to the other
            // team. The YES/NO side stays global to the ticket.
            team: seed?.team ?? selectedSide,
            price,
            qty: seed?.qty ?? String(defaultContractsPref),
          },
        ];
      });
    },
    [defaultContractsPref, formPrice, selectedSide],
  );
  const updateLadderLeg = useCallback(
    (id: string, patch: Partial<Pick<LadderLeg, 'price' | 'qty' | 'team'>>) => {
      setExtraLegs((legs) => legs.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    },
    [],
  );
  const removeLadderLeg = useCallback((id: string) => {
    setExtraLegs((legs) => legs.filter((l) => l.id !== id));
  }, []);
  const clearLadderLegs = useCallback(() => setExtraLegs([]), []);
  // Safety: a rung's price is only meaningful for the game / venue / YES-NO
  // side it was typed against. Drop the ladder when any of those change so
  // stale prices can't be fired at a different book. NOT cleared on the team
  // toggle — rungs are team-tagged and both teams are valid targets.
  //
  // #348 — KEPT AS AN EFFECT. The obvious derivation is to stamp the rungs with
  // the context they were typed against and read only the matching ones. That
  // reads NON-destructively, so leaving a context and returning revives the
  // ladder, where this effect destroys it on the way out. Reviving typed prices
  // on a trading ticket is an order-safety semantic change, and this issue's
  // non-goals rule that out ("No order semantic change"); the destroying half
  // has no render-time expression. Suppressed, not derived, until that change
  // is approved on its own issue with trading-safety evidence.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    clearLadderLegs();
  }, [game.key, panelVenue, panelSide, clearLadderLegs]);

  // Oscar release-and-overtake intent, armed from a blocker row in
  // GameOpenOrders (a sibling — hence lifted here). Arming loads the ticket:
  // venue → kalshi, type → limit, side → the take side, price → the protected
  // level (through the external-write path so it glows + autofills qty).
  const [oscarReleaseIntent, setOscarReleaseIntent] = useState<OscarReleasePrefill | null>(null);
  const clearOscarReleaseIntent = useCallback(() => setOscarReleaseIntent(null), []);
  const armOscarRelease = useCallback(
    (prefill: OscarReleasePrefill) => {
      setPanelVenue('kalshi');
      setFormOrderType('limit');
      setPanelSide(prefill.takeSide);
      writeFormPriceFromExternal(String(prefill.takePriceCents));
      setOscarReleaseIntent(prefill);
    },
    [writeFormPriceFromExternal],
  );
  // Staleness: a different game invalidates the armed intent outright; a
  // venue/side flip AWAY from the intent's own target does too. (Arming
  // itself sets side === takeSide, so this effect doesn't clear the arm it
  // just created.)
  //
  // #348 — KEPT AS EFFECTS, for the same reason as the ladder clear above: a
  // stamp-and-predicate form only HIDES the arm, so a venue, side or game
  // round-trip would re-arm a release the trader had already lost. That is an
  // order-safety semantic change, out of scope here. Suppressed, not derived.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOscarReleaseIntent(null);
  }, [game.key]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOscarReleaseIntent((prev) =>
      prev && (panelVenue !== 'kalshi' || panelSide !== prev.takeSide) ? null : prev,
    );
  }, [panelVenue, panelSide]);

  // Pull a fresh price hint from the newly-selected team's book +
  // current YES/NO toggle and push it through the external-write path
  // (which also bumps priceFlashKey for the glow). Without this, a
  // trader who switches teams keeps the stale price typed into the
  // input — easy way to fire an order at the wrong number. Reads from
  // the panel's current venue so a Poly-side trader keeps seeing Poly
  // prices on team swap.
  const applyTeamToggle = useCallback(
    (next: TeamSide) => {
      setSelectedSide(next);
      onTeamSideChange?.(next);
      const venueBooks = panelVenue === 'kalshi' ? game.kalshi : game.polymarket;
      const nextBook = next === 'away' ? venueBooks?.away : venueBooks?.home;
      const hint = bestPriceCentsForOrderSide(nextBook ?? undefined, panelSide);
      if (hint !== undefined) {
        writeFormPriceFromExternal(String(hint));
      }
    },
    [game.kalshi, game.polymarket, panelVenue, panelSide, onTeamSideChange, writeFormPriceFromExternal],
  );

  // Venue swap — same idea as team swap: refresh the price hint from
  // the new venue's book on the currently-selected team + side, and
  // glow the input so the trader can't miss that the order is now
  // routing somewhere else.
  const applyVenueToggle = useCallback(
    (next: 'kalshi' | 'polymarket') => {
      setPanelVenue(next);
      const venueBooks = next === 'kalshi' ? game.kalshi : game.polymarket;
      const nextBook = selectedSide === 'away' ? venueBooks?.away : venueBooks?.home;
      const hint = bestPriceCentsForOrderSide(nextBook ?? undefined, panelSide);
      if (hint !== undefined) {
        writeFormPriceFromExternal(String(hint));
      }
    },
    [game.kalshi, game.polymarket, selectedSide, panelSide, writeFormPriceFromExternal],
  );

  // Venue availability — used to disable the tab for a venue that has
  // no book on this game at all (e.g. spread/total events on Poly).
  const kalshiAvailable = !!(game.kalshi?.away || game.kalshi?.home);
  const polyAvailable = !!(game.polymarket?.away || game.polymarket?.home);

  // Depend on the primitive fields, NOT the tradeIntent object reference.
  // Callers (especially GameDetail) rebuild a fresh tradeIntent literal
  // on every render via tradeCtxToTradeIntent, so a `[tradeIntent]` dep
  // would fire on every parent render and stomp the user's local
  // team-tab / venue-tab click. Primitives change only when the actual
  // value changes.
  const tradeIntentSide = tradeIntent?.side;
  const tradeIntentVenue = tradeIntent?.venue;
  const tradeIntentPrefillPrice = tradeIntent?.prefillPrice;
  const tradeIntentInitialOrderSide = tradeIntent?.initialOrderSide;
  const tradeIntentGameKey = tradeIntent?.gameKey;
  const tradeIntentInteractionSequence = tradeIntent?.interactionSequence;
  //
  // #348 — KEPT AS AN EFFECT, deliberately. A trade intent is an external
  // COMMAND ("open this ticket on the Poly away book at 61¢"), not a value with
  // a render-time expression: after it lands the trader is free to move every
  // control it touched, so there is nothing to derive from. It also drives
  // `writeFormPriceFromExternal`, which bumps `priceFlashKey` to glow the input
  // — a transient animation trigger whose own state lives in
  // useOrderEntryModel (`priceFlashing`), outside this cluster.
  //
  // The one disable below is load-bearing: the write has no derived form.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tradeIntentSide) setSelectedSide(tradeIntentSide);
    if (tradeIntentVenue) setPanelVenue(tradeIntentVenue);
    // Skip a stale prefill (tagged for a different game/line) so the price
    // seeds from the new book's best bid instead. See prefillMatchesGame.
    if (
      tradeIntentPrefillPrice != null &&
      (tradeIntentGameKey == null || tradeIntentGameKey === game.key)
    ) {
      writeFormPriceFromExternal(String(tradeIntentPrefillPrice));
    }
    if (tradeIntentInitialOrderSide) {
      setPanelSide(tradeIntentInitialOrderSide);
    }
  }, [
    game.key,
    tradeIntentSide,
    tradeIntentVenue,
    tradeIntentPrefillPrice,
    tradeIntentInitialOrderSide,
    tradeIntentGameKey,
    tradeIntentInteractionSequence,
    writeFormPriceFromExternal,
  ]);

  // Active book's ticker for the panel's current (venue × team). When
  // a spread/total chip is picked anywhere — the dashboard's chip
  // strip, the panel's own MiniLineStrip — the synth game gets a new
  // line ticker, so this primitive changes while the panel stays
  // mounted. Treat that transition as an external price write: bump
  // priceFlashKey (input glows + qty default fills if blank) and
  // write the best-side hint from the new book. Same UX as clicking
  // a pill — chip swap should land an order ready to submit.
  //
  // The reseed-vs-keep decision is delegated to
  // shouldReseedPriceOnBookChange — see its doc for why a team/venue
  // switch must NOT reseed (it would clobber a just-clicked ladder
  // level with top-of-book). The ref carries (ticker, side, venue) so
  // the predicate can tell a line swap from a selection switch.
  const activeBookForFormRef = useRef<ActiveBookSelection | null>(null);
  const activeBookForForm = activeVenueBook(game, selectedSide, panelVenue) ?? undefined;
  const activeBookTicker = activeBookForForm?.marketTicker ?? null;
  //
  // #348 — KEPT AS AN EFFECT, deliberately. This is an edge detector over the
  // PREVIOUS book selection (the ref above), firing a one-shot write plus the
  // input glow. "A new line just landed" is a transition, not a value: there is
  // no render-time expression for it, and the glow's own state (`priceFlashing`)
  // lives in useOrderEntryModel, outside this cluster. Deriving the write
  // instead would fire `react-hooks/set-state-in-render`, which is absent from
  // lint-budget.json and therefore fails the gate outright.
  useEffect(() => {
    const prev = activeBookForFormRef.current;
    const next = { ticker: activeBookTicker, side: selectedSide, venue: panelVenue };
    activeBookForFormRef.current =
      activeBookTicker === null
        ? null
        : { ticker: activeBookTicker, side: selectedSide, venue: panelVenue };
    if (!shouldReseedPriceOnBookChange(prev, next)) return;
    const hint = bestPriceCentsForOrderSide(activeBookForForm ?? undefined, panelSide);
    if (hint !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      writeFormPriceFromExternal(String(hint));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBookTicker]);

  // Ask Hydra to subscribe upstream to this game's Kalshi orderbooks while the
  // panel is open. No-op until Hydra serves /api/* over HTTPS — see
  // docs/hydra-server-asks.md.
  useHydraOrderbookWarmup([game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker]);

  // Register the panel's effective tickers via its own orderbook-needs
  // channel so depth follows the panel chip even when it has diverged
  // from the dashboard's selection. Deduped against `spreads-totals` and
  // `moneyline` channels by the store.
  const panelKalshiTicker = game.kalshi?.away?.marketTicker ?? null;
  const panelPolyTicker = game.polymarket?.away?.marketTicker ?? null;
  useEffect(() => {
    const tickers: string[] = [];
    if (panelKalshiTicker) tickers.push(panelKalshiTicker);
    if (panelPolyTicker) tickers.push(panelPolyTicker);
    useMarketStore.getState().setOrderbookNeeds('detail-panel', tickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('detail-panel', []);
    };
  }, [panelKalshiTicker, panelPolyTicker]);

  const teamName = bookCaptionName(game, selectedSide);
  const kalshiBook = selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
  const polyBook = selectedSide === 'away' ? game.polymarket?.away : game.polymarket?.home;
  // Soccer 3-way rows ride the betLineContext panel mode, but unlike
  // spread/total synths their price strip is wanted (Joshua, Jun 12 call:
  // K/P missing on World Cup quick-order). The synth dupes one outcome
  // book into both slots, so the strip needs outcome/complement columns
  // (NO ladder on the right) instead of away/home — see the strip below.
  const isMultiSynth = gameMarketKind(game) === 'multi';
  // Side-panel book depth — user preference (Settings → Display). Default 8
  // keeps today's fan; Prof's tighter view is 3 per side.
  const bookDepthLevels = useDisplaySettings((st) => st.bookDepthLevels);
  // Pinned (default): ticket fixed, only the order book scrolls. Off: the whole
  // panel scrolls as one column (rescues short/laptop viewports). See displaySettings.
  const pinnedOrderTicket = useDisplaySettings((st) => st.pinnedOrderTicket);
  const setPinnedOrderTicket = useDisplaySettings((st) => st.setPinnedOrderTicket);

  // Drive bids/asks off panelSide, with the same phantom-level sanity filter
  // the inline ladder + Yes/No pills use so this panel matches them at the
  // spread.
  //   YES side: bids = book.yes (yes bids), asks = 100 − no bids
  //   NO  side: bids = book.no  (no bids),  asks = 100 − yes bids
  const kalshiSide = buildSide(kalshiBook, panelSide);
  const polySide = buildSide(polyBook, panelSide);

  // #667 — is there actionable depth to protect the top of the rail for?
  //
  // Keyed on the levels the book WILL RENDER, not on any connection flag: a
  // disconnected Kalshi ACCOUNT is a credential fact and says nothing about the
  // market-data feed, which is exactly why the disconnected panel still draws a
  // full live ladder. With depth, the book earns the space under the ticket and
  // the receipts sit below it; with none, there is nothing to protect and the
  // receipts take the inline slot beside Order Entry.
  //
  // This is also what makes the placement flutter-proof, and the reason no
  // debounce or latch is needed: marketStore holds the last book per ticker in
  // memory and a dropped subscription leaves those levels standing (the same
  // retain-last behaviour that makes a dead feed render stale prices). A brief
  // reconnect therefore never empties the ladder, so it never moves the card.
  // Only a book that is genuinely absent or genuinely empty reads as no depth.
  const bookHasDepth =
    kalshiSide.bids.length > 0 ||
    kalshiSide.asks.length > 0 ||
    polySide.bids.length > 0 ||
    polySide.asks.length > 0;

  return (
    /* The panel is its own pocket universe: the ROOT always carries the
       scroll (overflow-y-auto), so neither layout mode can strand content
       off the bottom of a short screen — anything below the fold is
       reachable by scrolling the panel, never the page.
       - Compact (pinnedOrderTicket): today's classic layout — ticket in
         view, books filling the remainder with their own internal scroll.
         On a normal screen the column exactly fills the root, so the root
         has nothing to scroll and the panel renders identically to the
         old pinned mode; the root only gains scroll when the fixed
         sections alone exceed the viewport (short laptops).
       - Expanded: everything renders full height; the root scrolls the
         whole column (the old "scroll whole panel" mode).
       The w-2 gutter on the right edge belongs to the ROOT scroll
       context: a quiet lane where the wheel always drives the panel
       itself, even when the cursor would otherwise sit over a nested
       book scroller. */
    <div className="bg-card/95 border-border flex h-full w-96 min-h-0 overflow-y-auto border-l">
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          // Compact: h-full pins the column to the viewport (books flex-fill
          // the remainder — the classic look); min-h-fit lets it grow past
          // h-full when header+ticket alone are taller than the screen, which
          // is what hands the overflow to the root scroller.
          pinnedOrderTicket ? 'h-full min-h-fit' : 'min-h-full',
        )}
      >
      {/* Header — Spread/Total renders the bet-description title with a chip
        * strip in place of the matchup. The away/home toggle below is hidden
        * since both slots point at the same synthesized VenueBook. */}
      {betLineContext ? (
        <div className="border-border space-y-2 border-b px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-foreground text-sm font-semibold leading-snug">
                {betLineContext.titleFor(panelSide, panelChipIdx)}
              </h3>
              <p className="text-muted-foreground mt-0.5 font-mono text-[11px]">
                {game.awayCode} @ {game.homeCode} &middot; {game.date}
              </p>
            </div>
            {/* Stacked vertically — X on top, layout toggle beneath — so the
                pair hugs the corner without jutting into the LIVE badge. */}
            <div className="flex shrink-0 flex-col items-center gap-0.5">
              {onClose && (
                <button onClick={onClose} className="text-muted-foreground hover:bg-accent hover:text-foreground rounded p-1">
                  <X size={16} />
                </button>
              )}
              <button
                onClick={() => setPinnedOrderTicket(!pinnedOrderTicket)}
                title={
                  pinnedOrderTicket
                    ? 'Expand cards — whole panel scrolls as one column'
                    : 'Compact cards — ticket in view, books scroll inside'
                }
                className={`hover:bg-accent rounded p-1 ${
                  pinnedOrderTicket
                    ? 'text-muted-foreground hover:text-foreground'
                    : 'text-primary hover:text-primary'
                }`}
              >
                {pinnedOrderTicket ? <UnfoldVertical size={16} /> : <FoldVertical size={16} />}
              </button>
            </div>
          </div>
          <MiniLineStrip
            chips={betLineContext.chips}
            selectedIdx={panelChipIdx}
            onSelect={handlePanelChipSelect}
          />
        </div>
      ) : (
        <>
          {/* Moneyline header */}
          <div className="border-border flex items-center justify-between border-b px-4 py-3">
            <div>
              <h3 className="text-foreground flex items-center gap-2 text-sm font-semibold">
                <span>
                  {game.awayName} @ {game.homeName}
                </span>
                {/* #121 — was `game.gameState === 'live'`, marketStore's
                    "book tradable AND clock past the scheduled start"
                    heuristic, which claims LIVE on a postponed game for as
                    long as its book stays open. Resolves through the shared
                    helper instead, from the same exact OG Hydra game-state
                    record as the table and warning surface. A missing or stale
                    producer record still renders neutral UNKNOWN, never a
                    clock-derived LIVE claim. */}
                {(() => {
                  const chipState = resolveLiveDisplayState(canonicalGameState, game.gameState);
                  if (chipState === 'live') return <LiveBadge />;
                  if (chipState === 'unknown') return <UnknownStatusBadge />;
                  return null;
                })()}
              </h3>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {game.date} &middot; {game.timePt} PT
              </p>
            </div>
            {/* Stacked vertically — X on top, layout toggle beneath — so the
                pair hugs the corner without jutting into the LIVE badge. */}
            <div className="flex shrink-0 flex-col items-center gap-0.5">
              {onClose && (
                <button onClick={onClose} className="text-muted-foreground hover:bg-accent hover:text-foreground rounded p-1">
                  <X size={16} />
                </button>
              )}
              <button
                onClick={() => setPinnedOrderTicket(!pinnedOrderTicket)}
                title={
                  pinnedOrderTicket
                    ? 'Expand cards — whole panel scrolls as one column'
                    : 'Compact cards — ticket in view, books scroll inside'
                }
                className={`hover:bg-accent rounded p-1 ${
                  pinnedOrderTicket
                    ? 'text-muted-foreground hover:text-foreground'
                    : 'text-primary hover:text-primary'
                }`}
              >
                {pinnedOrderTicket ? <UnfoldVertical size={16} /> : <FoldVertical size={16} />}
              </button>
            </div>
          </div>

          {/* Team selector — moneyline only. */}
          <div className="border-border flex border-b">
            <button
              className={cn(
                'flex-1 py-2 text-center text-sm font-medium transition-colors',
                selectedSide === 'away'
                  ? 'bg-primary/20 text-primary border-primary border-b-2'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => applyTeamToggle('away')}
            >
              {game.awayName}
            </button>
            <button
              className={cn(
                'flex-1 py-2 text-center text-sm font-medium transition-colors',
                selectedSide === 'home'
                  ? 'bg-primary/20 text-primary border-primary border-b-2'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => applyTeamToggle('home')}
            >
              {game.homeName}
            </button>
          </div>
        </>
      )}

      {/* Venue selector — the most prominent control on the panel.
          Mounted above OrderEntryForm so the trader can't miss which
          venue their order is routing to. Disabled side falls
          unclickable when the game has no book on that venue (e.g.
          spread/total on Poly today). Rendered in both moneyline and
          betLineContext layouts so the choice is always visible. */}
      <div className="border-border flex border-b">
        <button
          type="button"
          disabled={!kalshiAvailable}
          onClick={() => applyVenueToggle('kalshi')}
          className={cn(
            'flex-1 py-2 text-center text-xs font-bold uppercase tracking-wider transition-colors',
            panelVenue === 'kalshi'
              ? 'bg-blue-500/20 text-blue-300 light:text-blue-700 border-blue-500 border-b-2'
              : !kalshiAvailable
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Kalshi
        </button>
        <button
          type="button"
          disabled={!polyAvailable}
          onClick={() => applyVenueToggle('polymarket')}
          className={cn(
            'flex-1 py-2 text-center text-xs font-bold uppercase tracking-wider transition-colors',
            panelVenue === 'polymarket'
              ? 'bg-purple-500/20 text-purple-300 light:text-purple-700 border-purple-500 border-b-2'
              : !polyAvailable
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Polymarket
        </button>
      </div>

      {/* Order Entry — wrapped with the bot mode-row. The row only appears for
          bot-access traders on a Kalshi market; otherwise the order form below
          renders unchanged (default mode = Manual). */}
      <BotModeSwitch
        ticker={
          panelVenue === 'kalshi'
            ? ((selectedSide === 'away' ? game.kalshi?.away?.marketTicker : game.kalshi?.home?.marketTicker) ?? null)
            : null
        }
        pair={(() => {
          // Pair MM (in-relay sum-pair bot) needs BOTH sibling game markets of
          // one event. The sportOfEventTicker gate keeps series-winner rows
          // (KX*SERIES clinchers) out — only *GAME/MATCH series qualify.
          if (panelVenue !== 'kalshi') return null;
          const away = game.kalshi?.away?.marketTicker;
          const home = game.kalshi?.home?.marketTicker;
          if (!away || !home || away === home) return null;
          const ev = eventTickerOfMarket(away);
          if (ev !== eventTickerOfMarket(home) || sportOfEventTicker(ev) == null) return null;
          return {
            eventTicker: ev,
            tickerAway: away,
            tickerHome: home,
            ...(game.kalshi?.away?.exchangeIndex !== undefined
              ? { exchangeIndexAway: game.kalshi.away.exchangeIndex }
              : {}),
            ...(game.kalshi?.home?.exchangeIndex !== undefined
              ? { exchangeIndexHome: game.kalshi.home.exchangeIndex }
              : {}),
            awayCode: game.awayCode,
            homeCode: game.homeCode,
          };
        })()}
      >
        <OrderEntryForm
          game={game}
          selectedSide={selectedSide}
          venue={panelVenue}
          orderSide={panelSide}
          onOrderSideChange={setPanelSide}
          price={formPrice}
          onPriceChange={setFormPrice}
          priceFlashKey={priceFlashKey}
          orderType={formOrderType}
          onOrderTypeChange={setFormOrderType}
          extraLegs={extraLegs}
          onAddLeg={addLadderLeg}
          onUpdateLeg={updateLadderLeg}
          onRemoveLeg={removeLadderLeg}
          onClearLegs={clearLadderLegs}
          oscarReleaseIntent={oscarReleaseIntent}
          onClearOscarReleaseIntent={clearOscarReleaseIntent}
          // undefined (not null) when there is no depth: that is the prop's
          // "render inline" state, and it is what puts the cards beside Order
          // Entry when no book is competing for the top of the rail.
          receiptsHost={bookHasDepth ? receiptsHost : undefined}
        />
      </BotModeSwitch>

      {/* Orderbooks. Clicking any level routes the ticket to the
          venue that was clicked + writes the price (and order type
          where applicable). Lets the trader pick a target by tapping
          the depth instead of manually toggling the venue tab. */}
      {/* #667 — the book is the trade surface, so it sits directly under the
          ticket and everything else sits below it. Compact: flex-1 still fills
          the viewport remainder; min-h-96 is the floor a fully populated rail
          falls back to, sized to a readable two-sided book rather than the
          192px strip that made a busy rail untradeable. Overflow past the
          floor goes to the root scroller, which every section stays inside. */}
      <div
        data-testid="game-detail-books"
        className={cn('space-y-3 p-3', pinnedOrderTicket && 'min-h-96 flex-1 overflow-y-auto')}
      >
        {kalshiBook && (
          <ExchangeOrderBook
            venue="kalshi"
            maxLevels={bookDepthLevels}
            teamLabel={`${betLineContext ? '' : `${teamName} — `}${panelSide === 'yes' ? 'YES' : 'NO'}`}
            headerRight={<OrderbookFreshness ticker={kalshiBook?.marketTicker} />}
            bids={kalshiSide.bids}
            asks={kalshiSide.asks}
            onLevelClick={(info) => {
              // Switch the form to Kalshi first — the trader may have
              // been on Poly and clicked into K's depth. Any level click
              // → limit at that price, matching the value-dash pill
              // path: an ask click is a marketable limit that fills the
              // displayed depth and rests the remainder at that price.
              // (Was a 'market' order on ask clicks — that swept the
              // whole book with no price cap and left nothing resting.)
              setPanelVenue('kalshi');
              // Ladder mode (limit + at least one extra rung): a bid tap adds
              // a rung at that price instead of overwriting the base input, so
              // a trader can build the whole ladder straight from the book.
              // Ask taps are ignored while laddering — the ladder is limit-only.
              const ladderMode = formOrderType === 'limit' && extraLegs.length > 0;
              if (info.side === 'bid') {
                if (ladderMode) {
                  addLadderLeg({ price: String(info.priceCents) });
                } else {
                  setFormOrderType('limit');
                  writeFormPriceFromExternal(String(info.priceCents));
                }
              } else if (!ladderMode) {
                setFormOrderType('limit');
                writeFormPriceFromExternal(String(info.priceCents));
              }
            }}
          />
        )}
        {polyBook && (
          <ExchangeOrderBook
            venue="polymarket"
            maxLevels={bookDepthLevels}
            teamLabel={`${betLineContext ? '' : `${teamName} — `}${panelSide === 'yes' ? 'YES' : 'NO'}`}
            headerRight={<OrderbookFreshness ticker={polyBook?.marketTicker} />}
            bids={polySide.bids}
            asks={polySide.asks}
            onLevelClick={(info) => {
              // Route the ticket to Polymarket. Poly's relay doesn't
              // support market orders today, so an ask click becomes a
              // takeable limit at the ask price rather than a market
              // type — same final fill behavior, just a different
              // order shape the relay accepts.
              setPanelVenue('polymarket');
              setFormOrderType('limit');
              writeFormPriceFromExternal(String(info.priceCents));
            }}
          />
        )}
      </div>

      {/* #667 — the ticket portals its durable Oscar/one-click receipts here,
          directly under the book. `empty:hidden` keeps the rail from carrying a
          bordered gap on a game that has none. */}
      <div
        ref={setReceiptsHost}
        data-testid="rail-receipts-host"
        className="border-border empty:hidden space-y-3 border-b px-4 py-3"
      />

      {/* Open orders for THIS game — cancel inline without going to the
          positions tab (StreamRift trading-safety ask #2/#3). */}
      <GameOpenOrders game={game} onOscarRelease={armOscarRelease} />

      {/* Held contracts on the EXACT market selected above (#280) — canonical
          per-market qty + average entry, both venues. Read-only, no P&L.
          Sits above the pair card: per-market fact first, match-level blended
          context second. */}
      <GamePositionSnapshot game={game} />

      {/* Match-level position context (live-tennis V1c) — qty/avg per
          outcome, paired qty + blended pair cost, residual exposure. Renders
          only when this match carries open Kalshi contracts. */}
      <PairContextCard game={game} />

      {/* Per-team price summary \u2014 moneyline only; for synthetic spread/total
        * GameRows the away/home columns would show identical numbers.
        * U (Unabated consensus) row only renders when the caller supplies
        * consensus values \u2014 GameDetail wires it through useOddsStream;
        * the value dashboard caller passes nothing so the row's hidden
        * there. YES/NO toggle (panelSide) flips the consensus the same
        * way K and P flip: NO = 100 \u2212 fairProb. */}
      {(!betLineContext || isMultiSynth) && (
        <div data-testid="price-summary-strip" className="border-border border-b px-4 py-2">
          <div className="grid grid-cols-3 gap-2">
            <div />
            <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{isMultiSynth ? game.awayName : game.awayCode}</div>
            <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{isMultiSynth ? game.homeName : game.homeCode}</div>
          </div>
          {/* U row stays match-level away/home — wrong frame for an
              outcome/complement pair (nonsensical for Draw), so multi
              hides it until a per-outcome consensus lands. */}
          {!isMultiSynth && (unabated?.awayCents != null || unabated?.homeCents != null) && (
            <UnabatedConsensusRow unabated={unabated} />
          )}
          {/* OP row — IMMEDIATELY below U (architecture §8). Independent
              of the U row's presence: an Unabated outage leaves OP
              rendering, and vice versa. Hidden on multi synth panels for
              the same reason the U row is (match-level away/home frame is
              wrong for an outcome/complement pair). */}
          {!isMultiSynth && opOdds != null && <OpOddsRow opOdds={opOdds} trend={opOddsTrend} />}
          {/* K and P rows render YES prices regardless of the panel's
              YES/NO toggle. The whole strip is meant to read like
              "each venue's view of each team's win probability" — all
              three rows directional same way, so a glance at the
              column tells you who's favored across all three sources.
              Active-side prices (the ones the trader is about to pay)
              live in the orderbook below + the trade ticket above. */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-xs font-semibold text-blue-400">K</div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.kalshi?.away, 'yes')}
            </div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.kalshi?.home, isMultiSynth ? 'no' : 'yes')}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-xs font-semibold text-purple-400">P</div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.polymarket?.away, 'yes')}
            </div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.polymarket?.home, isMultiSynth ? 'no' : 'yes')}
            </div>
          </div>
        </div>
      )}

      </div>
      {/* Scroll gutter — owned by the root scroller, so the wheel here
          always moves the panel. Deliberately quiet: a hairline lane with
          a whisper of hover feedback, discoverable by feel. STICKY, not
          stretched: a stretched flex child only spans the container (one
          viewport) and scrolls away with the content; sticky + h-full
          keeps the lane pinned to the visible band at every scroll
          position (verified live on g1.pm — stretched lane left the
          screen past one viewport of scroll). */}
      <div
        aria-hidden
        title="Scroll panel"
        className="border-border/20 hover:bg-muted/40 sticky top-0 h-full w-2 shrink-0 self-start border-l transition-colors"
      />
    </div>
  );
}
